import { SettingEntry } from '@/services/Settings/getSettings'

import {
  CustomizationElementSettingsFormValues,
  CustomizationElementSettingsPayload,
  ELEMENT_SIZE_SETTING_KEY,
  ElementSize,
  MIN_RESOLUTION_SETTING_KEY,
  MinimumResolution,
} from './types'

const DEFAULT_ELEMENT_SIZE: ElementSize = {
  text: 90,
  graphic: 90,
  ai_image: 90,
  monogram: 90,
  upload_graphic: 90,
}

const DEFAULT_MINIMUM_RESOLUTION: MinimumResolution = {
  width: 300,
  height: 300,
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const getPositiveNumber = (value: unknown, fallback: number): number => {
  const numberValue = typeof value === 'number' || typeof value === 'string' ? Number(value) : Number.NaN

  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback
}

const readElementSize = (value: unknown): ElementSize => {
  const elementSize = isRecord(value) ? value : {}

  return {
    text: getPositiveNumber(elementSize.text, DEFAULT_ELEMENT_SIZE.text),
    graphic: getPositiveNumber(elementSize.graphic, DEFAULT_ELEMENT_SIZE.graphic),
    ai_image: getPositiveNumber(elementSize.ai_image, DEFAULT_ELEMENT_SIZE.ai_image),
    monogram: getPositiveNumber(elementSize.monogram, DEFAULT_ELEMENT_SIZE.monogram),
    upload_graphic: getPositiveNumber(elementSize.upload_graphic, DEFAULT_ELEMENT_SIZE.upload_graphic),
  }
}

const readMinimumResolution = (value: unknown): MinimumResolution => {
  const minimumResolution = isRecord(value) ? value : {}

  return {
    width: getPositiveNumber(minimumResolution.width, DEFAULT_MINIMUM_RESOLUTION.width),
    height: getPositiveNumber(minimumResolution.height, DEFAULT_MINIMUM_RESOLUTION.height),
  }
}

export const DEFAULT_CUSTOMIZATION_ELEMENT_SETTINGS_FORM_VALUES: CustomizationElementSettingsFormValues = {
  textElementSize: DEFAULT_ELEMENT_SIZE.text,
  graphicElementSize: DEFAULT_ELEMENT_SIZE.graphic,
  aiImageElementSize: DEFAULT_ELEMENT_SIZE.ai_image,
  monogramElementSize: DEFAULT_ELEMENT_SIZE.monogram,
  uploadGraphicElementSize: DEFAULT_ELEMENT_SIZE.upload_graphic,
  minimumResolutionWidth: DEFAULT_MINIMUM_RESOLUTION.width,
  minimumResolutionHeight: DEFAULT_MINIMUM_RESOLUTION.height,
}

export const mapSettingsToFormValues = (settings: SettingEntry[]): CustomizationElementSettingsFormValues => {
  const elementSize = readElementSize(settings.find((setting) => setting.key === ELEMENT_SIZE_SETTING_KEY)?.value)
  const minimumResolution = readMinimumResolution(
    settings.find((setting) => setting.key === MIN_RESOLUTION_SETTING_KEY)?.value
  )

  return {
    textElementSize: elementSize.text,
    graphicElementSize: elementSize.graphic,
    aiImageElementSize: elementSize.ai_image,
    monogramElementSize: elementSize.monogram,
    uploadGraphicElementSize: elementSize.upload_graphic,
    minimumResolutionWidth: minimumResolution.width,
    minimumResolutionHeight: minimumResolution.height,
  }
}

export const mapFormValuesToSettings = (
  values: CustomizationElementSettingsFormValues
): CustomizationElementSettingsPayload => ({
  elementSize: {
    text: Number(values.textElementSize),
    graphic: Number(values.graphicElementSize),
    ai_image: Number(values.aiImageElementSize),
    monogram: Number(values.monogramElementSize),
    upload_graphic: Number(values.uploadGraphicElementSize),
  },
  minimumResolution: {
    width: Number(values.minimumResolutionWidth),
    height: Number(values.minimumResolutionHeight),
  },
})
